import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useScreenSize } from '../../../utils/media-query';
import Form, { ColCountByScreen, GroupItem, Item } from 'devextreme-react/form';
import { SysIRemisionesTemporales } from '../../../types/types.remissions';
import { useAppSelector } from '../../../contexts/hooks';
import useApiRemission from '../../../api/api.remmisions';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import { Button, LoadIndicator } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';
import { ConvertLocalDate } from '../../../utils/functions';
import 'devextreme-react/text-area';

interface MyComponentProps {
  title: string;
  visible: boolean;
  idEst: number;
  setVisible: () => void;
  setConfigTemRem: (isSaved: boolean, lrId?: number | null) => void;
}

const dataRemmisions: SysIRemisionesTemporales = {
  Rmt_Estado: true,
  Rmt_FechaInicio: new Date(),
  Rmt_FechaFin: new Date(),
};

const Index: React.FC<MyComponentProps> = ({ title, visible, idEst, setVisible, setConfigTemRem }) => {
  const refForm = useRef<Form>(null);
  const Studyes = useAppSelector((x) => x.configuration.Data?.Estudios);
  const Laboratories = useAppSelector((x) => x.configuration.Data?.ComboBox.find((x) => x.Name === 'SysA_LaboratoriosRemisiones')?.Data);
  const formRef = useRef<Form>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { isXSmall } = useScreenSize();
  const { SetTemporalRemision, getTemporalRemisions, CreateTemporalRemision } = useApiRemission();
  
  // Cargar datos de remisión temporal existente
  useEffect(() => {
    if (visible && idEst !== -1 && refForm.current) {
      const loadRemission = async () => {
        try {
          const existingRemissions = await getTemporalRemisions(idEst);
          
          if (existingRemissions && existingRemissions.length > 0) {
            // Cargar datos existentes
            const remission = existingRemissions[0];
            dataRemmisions.Rmt_Id = remission.Rmt_Id;
            dataRemmisions.Rmt_Es_Id = idEst;
            dataRemmisions.Rmt_Lr_Id = remission.Rmt_Lr_Id;
            dataRemmisions.Rmt_FechaInicio = remission.Rmt_FechaInicio ? new Date(remission.Rmt_FechaInicio) : new Date();
            dataRemmisions.Rmt_FechaFin = remission.Rmt_FechaFin ? new Date(remission.Rmt_FechaFin) : new Date();
            dataRemmisions.Rmt_Observaciones = remission.Rmt_Observaciones;
            dataRemmisions.Rmt_Estado = remission.Rmt_Estado;
          } else {
            // Resetear para nueva remisión
            delete dataRemmisions.Rmt_Id;
            dataRemmisions.Rmt_Es_Id = idEst;
            dataRemmisions.Rmt_Lr_Id = undefined;
            dataRemmisions.Rmt_FechaInicio = new Date();
            dataRemmisions.Rmt_FechaFin = new Date();
            dataRemmisions.Rmt_Observaciones = undefined;
            dataRemmisions.Rmt_Estado = true;
          }
          
          if (refForm.current) {
            refForm.current.instance.repaint();
          }
        } catch (error) {
          console.error('Error loading remission:', error);
          dataRemmisions.Rmt_Es_Id = idEst;
        }
      };
      
      loadRemission();
    }
  }, [visible, idEst, getTemporalRemisions]);

  const close = () => {
    formRef.current?.instance.resetValues();
    setVisible();
  };

  const onSaveClick = () => {
    if (!refForm.current?.instance.validate().isValid) return;
    onSave();
  };



  const onSave = async () => {
    setLoading(true);
    try {
      // Obtener el Lr_Id del formulario antes de guardar
      const selectedLrId = refForm.current?.instance.getEditor('Rmt_Lr_Id')?.option('value');
      
      // Convertir las fechas a hora colombiana antes de enviar
      const dataToSend = {
        ...dataRemmisions,
        Rmt_FechaInicio: dataRemmisions.Rmt_FechaInicio ? ConvertLocalDate(dataRemmisions.Rmt_FechaInicio) : undefined,
        Rmt_FechaFin: dataRemmisions.Rmt_FechaFin ? ConvertLocalDate(dataRemmisions.Rmt_FechaFin) : undefined,
      };
      
      let result;
      // Si existe Rmt_Id, editar; si no existe, crear nueva
      if (dataRemmisions.Rmt_Id) {
        result = await SetTemporalRemision(dataToSend);
      } else {
        result = await CreateTemporalRemision(dataToSend);
      }
      
      if (result === 107 || (typeof result === 'number' && result < 200)) {
        // Notificar que se guardó exitosamente y pasar el Lr_Id del formulario
        setConfigTemRem(true, selectedLrId);
        // Cerrar el modal
        close();
      } else {
        // Si falla, notificar que no se guardó
        setConfigTemRem(false);
      }
    } catch (error) {
      console.error('Error saving temporal remission:', error);
      setConfigTemRem(false);
    } finally {
      setLoading(false);
    }
  };

  const onCancelClick = useCallback(() => {
    close();
  }, [close]);
  return (
    <Popup title={title} visible={visible} showCloseButton={true} onHidden={onCancelClick} hideOnOutsideClick={true} fullScreen={isXSmall} width={500} wrapperAttr={{ class: `form-popup` }} height='auto'>
      <ToolbarItem toolbar='bottom' location='center'>
        <div className={`form-popup-buttons-container `}>
          <Button stylingMode='contained' type={loading ? 'normal' : 'default'} disabled={loading} onClick={onSaveClick}>
            <div style={{ padding: 8 }}>
              <span className='dx-button-text' style={{ marginRight: loading ? 10 : 0 }}>
                {loading ? formatMessage('SavingButton') : formatMessage('saveButton')}
              </span>
              <LoadIndicator className='button-indicator' height={15} width={15} style={{ marginLeft: 20, marginRigth: 20 }} visible={loading} />
            </div>
          </Button>
        </div>
      </ToolbarItem>
      <Form formData={dataRemmisions} ref={refForm} showValidationSummary={true}>
        <GroupItem colCount={2}>
          <ColCountByScreen xs={2} sm={2} md={2} lg={2} />
          <Item dataField='Rmt_Es_Id' label={{ text: 'Estudio' }} isRequired={true} colSpan={2} editorType='dxSelectBox' editorOptions={{ readOnly: true, items: Studyes, displayExpr: 'Es_Nombre', valueExpr: 'Es_Id' }} />
          {Laboratories && Laboratories?.length > 0 && <Item dataField='Rmt_Lr_Id' isRequired={true} label={{ text: 'Lab. Referencia' }} colSpan={2} editorType='dxSelectBox' editorOptions={{ items: Laboratories, displayExpr: Object.keys(Laboratories[0])[1], valueExpr: Object.keys(Laboratories[0])[0] }} />}
          <Item dataField='Rmt_FechaInicio' isRequired={true} editorType='dxDateBox' label={{ text: 'Fecha inicial' }} colSpan={1} />
          <Item dataField='Rmt_FechaFin' isRequired={true} editorType='dxDateBox' label={{ text: 'Fecha final' }} colSpan={1} />
          <Item dataField='Rmt_Observaciones' editorOptions={{ height: 80 }} editorType='dxTextArea' label={{ text: 'Observaciones' }} colSpan={2}></Item>
        </GroupItem>
      </Form>
    </Popup>
  );
};

export default Index;
